import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, X, Phone, PhoneOff } from 'lucide-react';

const SimpleAudioChat = ({ onClose }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [status, setStatus] = useState('Disconnected');
  const [transcript, setTranscript] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);

  const connect = async () => {
    try {
      setStatus('Getting microphone...');
      
      // Get microphone
      streamRef.current = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 24000,
          sampleSize: 16,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      setStatus('Connecting to server...');
      
      // Connect WebSocket to simple proxy
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = process.env.NODE_ENV === 'development' ? 'localhost:4001' : window.location.host;
      wsRef.current = new WebSocket(`${protocol}//${host}/simple-realtime`);
      
      wsRef.current.onopen = () => {
        // Connected to server
        setStatus('Connected');
        setIsConnected(true);
      };
      
      wsRef.current.onmessage = async (event) => {
        if (event.data instanceof Blob) {
          // Received audio blob
          // Skip if it's too small or likely not audio
          if (event.data.size < 100) {
            // Skipping small blob, likely not audio
            return;
          }
          
          try {
            const arrayBuffer = await event.data.arrayBuffer();
            
            // Check if this is actually JSON data sent as binary
            try {
              const text = new TextDecoder().decode(arrayBuffer);
              const json = JSON.parse(text);
              // Binary data was actually JSON
              // Handle as JSON message
              if (json.type === 'session.created' || json.type === 'session.updated') {
                setStatus('Session ready');
              }
              return;
            } catch (e) {
              // Not JSON, continue as audio
            }
            
            // Ensure byte length is even for Int16Array
            let audioData = new Uint8Array(arrayBuffer);
            if (audioData.length % 2 !== 0) {
              // Adjusting odd byte length for PCM16
              // Create new array with even length
              const newLength = audioData.length - 1;
              audioData = audioData.slice(0, newLength);
            }
            
            // Only process if we have actual audio data
            if (audioData.length > 0) {
              const audioContext = new (window.AudioContext || window.webkitAudioContext)();
              
              // Create audio buffer from PCM16 data
              const pcm16 = new Int16Array(audioData.buffer);
              const audioBuffer = audioContext.createBuffer(1, pcm16.length, 24000);
              const channelData = audioBuffer.getChannelData(0);
              
              // Convert PCM16 to Float32
              for (let i = 0; i < pcm16.length; i++) {
                channelData[i] = pcm16[i] / 32768.0;
              }
              
              // Play the audio
              const source = audioContext.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(audioContext.destination);
              source.start();
            }
            
          } catch (error) {
            console.error('Audio playback error:', error);
          }
        } else {
          try {
            const msg = JSON.parse(event.data);
            // Message received
            
            if (msg.type === 'ready') {
              setStatus('Ready');
            } else if (msg.type === 'session.created' || msg.type === 'session.updated') {
              setStatus('Session ready');
            } else if (msg.type === 'conversation.item.input_audio_transcription.completed') {
              setTranscript(prev => prev + '\nYou: ' + msg.transcript);
            } else if (msg.type === 'response.audio_transcript.delta') {
              // Handle streaming transcript
              setTranscript(prev => {
                // If this is the first delta for a new response, add Assistant prefix
                if (!prev.endsWith('Assistant: ')) {
                  return prev + '\nAssistant: ' + msg.delta;
                }
                return prev + msg.delta;
              });
            } else if (msg.type === 'response.audio_transcript.done') {
              setTranscript(prev => prev + '\nAssistant: ' + msg.transcript);
            } else if (msg.type === 'response.done') {
              setStatus('Ready');
            } else if (msg.type === 'error') {
              console.error('Server error:', msg);
              // Handle different error formats
              let errorMessage = 'Unknown error';
              if (typeof msg.error === 'string') {
                errorMessage = msg.error;
              } else if (msg.error?.message) {
                errorMessage = msg.error.message;
              } else if (msg.error?.error?.message) {
                errorMessage = msg.error.error.message;
              } else if (msg.message) {
                errorMessage = msg.message;
              }
              
              // Don't disconnect on all errors - some are recoverable
              if (errorMessage.includes('session') || errorMessage.includes('connection')) {
                setStatus('Error: ' + errorMessage);
                disconnect();
              } else {
                // Just log the error but stay connected
                console.warn('Non-fatal error:', errorMessage);
                setStatus('Ready');
              }
            }
          } catch (e) {
            console.error('Parse error:', e);
          }
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setStatus('Connection error - please try again');
        setIsConnected(false);
      };
      
      wsRef.current.onclose = () => {
        setIsConnected(false);
        setStatus('Disconnected');
      };
      
    } catch (error) {
      console.error('Connection failed:', error);
      setStatus('Failed to connect');
    }
  };

  const disconnect = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setIsRecording(false);
    setStatus('Disconnected');
  };

  const startRecording = async () => {
    if (!isConnected || !streamRef.current) return;
    
    try {
      // Use AudioContext to get PCM16 data
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      const source = audioContext.createMediaStreamSource(streamRef.current);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      
      let audioBufferCount = 0;
      let hasAudioData = false; // Track if we actually captured audio
      
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      processor.onaudioprocess = (e) => {
        if (!isRecording) return;
        
        const inputData = e.inputBuffer.getChannelData(0);
        
        // Always send audio, don't filter for silence
        // Convert Float32 to PCM16
        const pcm16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        
        // Convert to base64
        const uint8 = new Uint8Array(pcm16.buffer);
        let binary = '';
        for (let i = 0; i < uint8.byteLength; i++) {
          binary += String.fromCharCode(uint8[i]);
        }
        const base64 = btoa(binary);
        
        // Send to server
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'input_audio_buffer.append',
            audio: base64
          }));
          audioBufferCount++;
          window.hasAudioData = true; // Update the global flag
          
          // Log every 10 buffers to confirm audio is being sent
          if (audioBufferCount % 10 === 0) {
            console.log(`Sent ${audioBufferCount} audio buffers`);
          }
        }
      };
      
      // Store processor reference for cleanup
      window.audioProcessor = processor;
      window.audioContext = audioContext;
      window.audioBufferCount = 0;
      window.hasAudioData = false; // Initialize as false
      
      setIsRecording(true);
      setStatus('Recording...');
      
    } catch (error) {
      console.error('Failed to start recording:', error);
    }
  };

  const stopRecording = () => {
    setIsRecording(false);
    
    const hadAudio = window.hasAudioData;
    
    // Clean up audio processor
    if (window.audioProcessor) {
      window.audioProcessor.disconnect();
      window.audioProcessor = null;
    }
    if (window.audioContext) {
      window.audioContext.close();
      window.audioContext = null;
    }
    
    // Always commit when we stop recording
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'input_audio_buffer.commit'
      }));
      
      // Since VAD is disabled, manually trigger a response
      setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'response.create'
          }));
        }
      }, 100);
      
      setStatus('Processing...');
      
      // Reset status after a timeout if no response
      setTimeout(() => {
        if (isConnected) {
          setStatus('Ready');
        }
      }, 10000);
    }
    
    // Reset the flag
    window.hasAudioData = false;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Voice Chat (Simple Mode)</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              Status: <span className={isConnected ? 'text-green-600' : 'text-gray-600'}>
                {status}
              </span>
            </span>
            
            {!isConnected ? (
              <button
                onClick={connect}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                <Phone className="w-4 h-4 inline mr-2" />
                Connect
              </button>
            ) : (
              <button
                onClick={disconnect}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                <PhoneOff className="w-4 h-4 inline mr-2" />
                Disconnect
              </button>
            )}
          </div>
        </div>

        {/* Transcript */}
        <div className="p-4 h-64 overflow-y-auto bg-gray-50">
          <h3 className="text-sm font-semibold mb-2">Conversation:</h3>
          <pre className="whitespace-pre-wrap text-sm">
            {transcript || 'No conversation yet...'}
          </pre>
        </div>

        {/* Controls */}
        <div className="p-4 border-t">
          <div className="flex justify-center">
            {isConnected && (
              <button
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                onTouchStart={startRecording}
                onTouchEnd={stopRecording}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  isRecording
                    ? 'bg-red-500 text-white scale-110'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {isRecording ? (
                  <>
                    <MicOff className="w-5 h-5 inline mr-2" />
                    Recording... (release to stop)
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5 inline mr-2" />
                    Hold to Talk
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleAudioChat;